import React, { useCallback, useEffect, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import dynamic from 'next/dynamic';

import { useFeatures } from '@atc/features';
import { formatNumber } from '@atc/string-fns';

import {
    AccordionPanel,
    Button, Col,
    Glyphicon,
    MultiSelectDropdown, Row,
    Select,
} from 'reaxl';
import { sendClick } from 'reaxl-analytics';
import { compareTrimsClick } from 'reaxl-analytics-handlers';
import {
    FilterCheckboxLabel,
    FilterTitle,
} from 'reaxl-filters';

import { pauseAudioEye } from '@/utilities/pauseAudioEye';

import srpActiveInteractionDuck from '@/ducks/srp/srpActiveInteractionDuck.js';
import srpFiltersDuck from '@/ducks/srp/srpFiltersDuck.js';

import useSrpNavigation from '@/hooks/useSrpNavigation';

import BuyOnlineButtonContainer from '@/containers/srp/BuyOnlineButtonContainer';

import LazyComponent from '@/components/LazyComponent';

import FilterPlaceholder from './FilterPlaceholder';

export const CompareTrimsLink = dynamic(() => import(
    /* webpackChunkName: "CompareTrimsLink" */
    '@/components/CompareTrimsLink'
));
const CompareTrimsContainer = dynamic(() => import(
    /* webpackChunkName: "CompareTrimsContainer" */
    '@/containers/CompareTrimsContainer'
    , { ssr: false }
));

const compareTrimCta = (trimFilterOptions, index) => (
    <LazyComponent>
        <CompareTrimsLink
            index={index}
            trimFilterOptions={trimFilterOptions}
        />
    </LazyComponent>
);

/*
 * for each trim code selected, assign a ctaValue & ctaClick in order for an additional link to appear next to the See
 * More expansion link
 */
const getTrimCodeCompareOptions = ({
    modelFilterOptions,
    onCtaClick,
}) => {
    const trimCodeOptions = {};

    if (modelFilterOptions) {
        Object.entries(modelFilterOptions).forEach(([key, value], index) => {
            Object.assign(trimCodeOptions, {
                [key]: {
                    ...value,
                    extendedOption: compareTrimCta(value, index),
                    onExtendedOptionsClick: onCtaClick,
                },
            });
        });
    }

    return trimCodeOptions;
};

/**
 *  Filter By MakeModelTrim combo renders a group of select elements and custom styling
 *
 */
function MakeModelTrimFilter({
    handleShowExpansion = () => { },
    hideOptionCounts = false,
    onOptionChange = () => { },
    groupsLimit = 5,
}) {
    const {
        filter_pause_audioeye: [filterPauseAudioEye, { pauseDuration }],
        single_mmt: [enableSingleMMT],
    } = useFeatures([
        'filter_pause_audioeye',
        'single_mmt',
    ]);

    const dispatch = useDispatch();
    const navigateToSrp = useSrpNavigation();

    const handleTrimCtaClick = (event, trimOptions) => {
        sendClick(compareTrimsClick, event);

        const selectedValue = trimOptions?.value || trimOptions?.[0]?.value?.split('|')?.[0];

        dispatch(srpActiveInteractionDuck.creators.setKeys({
            activeModel: selectedValue,
            showTrimsModal: true,
        }));
    };

    const filtersValues = useSelector(srpFiltersDuck.selectors.getFiltersValues);
    // MMT filters
    const makeFilterOptions = useSelector(srpFiltersDuck.selectors.getMakeFilterOptions);
    const makeFilterValues = useSelector(srpFiltersDuck.selectors.getMakeFilterValues);

    const modelFilterOptionsRaw = useSelector(srpFiltersDuck.selectors.getModelFilterOptions);
    const modelFilterOptions = {
        ...getTrimCodeCompareOptions({
            modelFilterOptions: modelFilterOptionsRaw,
            onCtaClick: handleTrimCtaClick,
        }),
    };

    const seriesFilterOptions = useSelector(srpFiltersDuck.selectors.getSeriesFilterOptions);
    const trimFilterOptions = useSelector(srpFiltersDuck.selectors.getTrimFilterOptions);

    const modelFilterValues = useSelector(srpFiltersDuck.selectors.getModelFilterValues);
    const trimFilterValues = useSelector(srpFiltersDuck.selectors.getTrimFilterValues);

    const packageOptions = useSelector(srpFiltersDuck.selectors.getPackageFilterOptions);

    const filterName = 'makeCode';

    // expand the panel if active options inside of it
    useEffect(() => {
        if (makeFilterValues.length) {
            handleShowExpansion(filterName);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [makeFilterValues.length]);

    const isExpanded = useSelector((state) => srpFiltersDuck.selectors.isExpansionActive(state, filterName));
    const showFilter = isExpanded && Object.keys(makeFilterOptions).length !== 0;

    const makeCodeKey = 'makeCode';
    const modelCodeKey = 'modelCode';
    const seriesCodeKey = 'seriesCode';
    const trimCodeKey = 'trimCode';
    const packageKey = 'packages';

    const handleRemoveMake = useCallback(async (event, filter) => {
        if (filterPauseAudioEye) {
            pauseAudioEye(pauseDuration);
        }
        // update redux state with filter change
        dispatch(srpFiltersDuck.creators.deselectFilter(event, filter));

        dispatch(srpActiveInteractionDuck.creators.setKeys({
            activeModel: '',
        }));
        let expFilterValue = {};
        // remove the experience and make filter
        if (enableSingleMMT) {
            expFilterValue = {
                ...filtersValues,
            };
            expFilterValue.experience = null;
            expFilterValue.makeCode = [];
        }

        navigateToSrp({
            filtersValues: expFilterValue,
            isNewSearch: true,
            resetPagination: true,
            scroll: false,
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const [showNewGroup, setShowNewGroup] = useState(false);
    useEffect(() => {
        if (makeFilterValues.length > 0) {
            setShowNewGroup(false);
        } else {
            setShowNewGroup(true);
        }
    }, [makeFilterValues, setShowNewGroup]);

    const title = (
        <FilterTitle
            title="Make, Model & Trim"
            key="makeModelTrimTitle"
        />
    );

    const getFilterOptions = (options, label) => {
        if (hideOptionCounts) {
            return options;
        }

        const selectOptions = [];
        selectOptions.push({
            label,
            value: label,
        });

        const optionCount = (option) => {
            if (option.count) {
                return `(${formatNumber({ value: option.count })})`;
            }

            if (option.disabled) {
                return '(0)';
            }

            return '';
        };

        options?.map((option) => !makeFilterOptions?.value?.includes(option.value) && selectOptions.push({
            ...option,
            label: `${option.label} ${optionCount(option)}`,
        }));

        return selectOptions;
    };

    const handleButtonClick = () => {
        setShowNewGroup(true);
    };

    const handleRemoveClick = useCallback((value) => {
        const payload = {
            filter: {
                name: makeCodeKey,
                value,
                isMultiple: true,
            },
            value,
            isMultiple: true,
        };
        handleRemoveMake(payload);
    }, [handleRemoveMake]);

    const renderGroup = (makeCode, index) => {
        const models = modelFilterOptions[`${modelCodeKey}|${makeCode}`];
        const series = seriesFilterOptions[`${seriesCodeKey}|${makeCode}`];

        const updateOptionLabels = (options) => options && options.map((option) => ({
            ...option,
            label: <FilterCheckboxLabel
                label={option.label}
                count={option.count}
                moreInfo={option.moreInfo}
                isFiltersCollapsed={option.isFiltersCollapsed}
                filterBadge={option.filterBadge}
            />,
        }));

        const { label } = makeFilterOptions?.options?.find((option) => option.value === makeCode) || {};

        const filterLabel = label ?? `Vehicle ${index + 1}`;

        return (
            <Row
                key={`${makeCode}-${index}`}
            >
                <Col
                    className="padding-bottom-1"
                    xs={12}
                >
                    <div className="text-size-200 text-subdued-lighter margin-bottom-1 text-bold">
                        {filterLabel}
                        {label && (
                            <Button
                                className="pull-right"
                                bsStyle="text"
                                onClick={() => handleRemoveClick(makeCode)}
                                aria-label="Remove vehicle"
                                title="Remove vehicle"
                            >
                                <Glyphicon
                                    glyph="remove"
                                    className="text-gray"
                                />
                            </Button>
                        )}
                    </div>

                    {!label && (
                        <Select
                            id={makeFilterOptions.name}
                            onChange={(event) => onOptionChange(event, makeFilterOptions)}
                            options={getFilterOptions(makeFilterOptions?.options, 'Any Make')}
                            name={makeFilterOptions.name}
                            aria-label={`${filterLabel} Make`}
                            value={makeCode || ''}
                        />
                    )}

                    {series && (
                        <MultiSelectDropdown
                            onChange={(event) => onOptionChange(event, series)}
                            options={updateOptionLabels(series?.options)}
                            name={series?.name}
                            defaultSelectLabel="Any"
                            affixLabel="Series"
                            uiContext="filters"
                        />
                    )}

                    {label && (
                        <MultiSelectDropdown
                            onChange={(event) => onOptionChange(event, models)}
                            disabled={!models?.options}
                            options={updateOptionLabels(models?.options)}
                            name={models?.name}
                            defaultSelectLabel="Any"
                            affixLabel="Models"
                            uiContext="filters"
                        />
                    )}

                    {modelFilterValues[`${modelCodeKey}|${makeCode}`]?.map((model, modelIndex) => {
                        const trim = trimFilterOptions[`${trimCodeKey}|${model}`];
                        const trimOptions = trim?.options;
                        const showTrim = trimOptions && trimOptions.length > 0;

                        return showTrim && (
                            <MultiSelectDropdown
                                key={`${model}-trim-${modelIndex}`}
                                onChange={(event) => onOptionChange(event, trim)}
                                options={updateOptionLabels(trim?.options)}
                                name={trim?.name}
                                defaultSelectLabel="Any"
                                affixLabel={`${trim?.label}s`}
                                uiContext="filters"
                                checkboxCta={
                                    label && models?.extendedOption && (
                                        <Button
                                            bsStyle="text"
                                            className="text-size-200 margin-vertical-1"
                                            data-cmp="cntnr-extnd-opt"
                                            onClick={(event) => {
                                                models?.onExtendedOptionsClick(event, models);
                                            }}
                                        >
                                            {models?.extendedOption}
                                        </Button>
                                    )
                                }
                            />
                        );
                    })}

                    {modelFilterValues[`${modelCodeKey}|${makeCode}`]?.map((model) => (
                        trimFilterValues[`${trimCodeKey}|${model}`]?.map((trim, trimIndex) => {
                            const packageOpts = packageOptions[`${packageKey}|${trim}`];

                            return packageOpts && (
                                <MultiSelectDropdown
                                    key={`${model}-trim-${trimIndex}`}
                                    onChange={(event) => onOptionChange(event, packageOpts)}
                                    options={updateOptionLabels(packageOpts?.options)}
                                    name={packageOpts?.name}
                                    defaultSelectLabel="Any"
                                    affixLabel={`${packageOpts?.label}`}
                                    uiContext="filters"
                                />
                            );
                        })
                    ))}
                </Col>
            </Row>
        );
    };

    // Troubleshooting t.map TypeError issue
    if (makeFilterValues && !Array.isArray(makeFilterValues)) {
        if (typeof window !== 'undefined' && window.newrelic) {
            window.newrelic.addPageAction('FilterAction', {
                makeCodeKey,
                component: 'MakeModelTrimFilter',
                makes: makeFilterValues,
                typeof: typeof makeFilterValues,
                message: `Value for ${makeCodeKey} is not an array`,
            });
        } else {
            // eslint-disable-next-line no-console
            console.error(`Value for ${makeCodeKey} is not an array. value: ${makeFilterValues} | type: ${typeof makeFilterValues}`);
        }
    }

    let makeCodeValues = makeFilterValues;

    if (typeof makeCodeValues === 'string') {
        makeCodeValues = makeFilterValues.split(' ');
    }

    const renderMakeModelTrim = (
        <>
            {makeCodeValues?.map((makeCode, index) => (
                renderGroup(makeCode, index)
            ))}

            {showNewGroup && renderGroup(null, makeCodeValues?.length || 0)}

            <BuyOnlineButtonContainer />

            {makeCodeValues?.length < groupsLimit && !showNewGroup && !enableSingleMMT && (
                <Button
                    block
                    onClick={handleButtonClick}
                >
                    Add Another Make
                </Button>
            )}
        </>
    );

    return (
        <AccordionPanel
            data-cmp="MMTFilter"
            eventKey={filterName}
            title={title}
            contentPadding={5}
        >
            {!showFilter && <FilterPlaceholder uniqueKey={filterName} />}
            {showFilter && renderMakeModelTrim}
            <CompareTrimsContainer modal />
        </AccordionPanel>
    );
}

export default MakeModelTrimFilter;
